/** @jsxImportSource @emotion/react */
import React, { useCallback, useMemo } from 'react'
import { SwapOutlined } from '@ant-design/icons'
import {
  HomeTeam,
  VisitingTeam,
} from '@sportninja/common/types/PlayerAttendance'
import {
  snacksContainerStyle,
  snacksImageStyle,
  snacksLabelAndTextContainerStyle,
  snacksLabelStyle,
  snacksTextStyle,
  styledSwapOutlinedStyle,
} from '../styles'

const PLACEHOLDER_PLAYER = '/images/generic_placeholders/placeholder_player.png'

export interface VideographerInfoProps {
  isTeamRepOnTeam: boolean
  team: HomeTeam | VisitingTeam
  teamNameSuffix: string
  onPressManageVideographer: (
    gameId: string,
    rosterId: string,
    teamId: string,
    teamName: string,
    teamSide: 'home' | 'visitor'
  ) => void
  gameId: string
  showVideo: boolean
  teamSide: 'home' | 'visitor'
}

const VideographerInfo: React.FC<VideographerInfoProps> = ({
  isTeamRepOnTeam,
  team,
  teamNameSuffix,
  onPressManageVideographer,
  gameId,
  showVideo,
  teamSide,
}) => {
  const handleClick = useCallback(() => {
    if (!isTeamRepOnTeam) {
      return
    }
    onPressManageVideographer(
      gameId,
      team?.game_roster_id,
      team?.id,
      team?.name,
      teamSide
    )
  }, [isTeamRepOnTeam, onPressManageVideographer, gameId, team, teamSide])

  const displayText = useMemo(() => {
    const videographers = team?.videographer_users
    if (!videographers || videographers.length === 0) {
      return isTeamRepOnTeam ? 'Choose a Person' : 'Not Set'
    }
    const firstName =
      videographers[0].name_full ||
      `${videographers[0].name_first} ${videographers[0].name_last}`.trim()
    if (videographers.length > 1) {
      return `${firstName} + ${videographers.length - 1}`
    }
    return firstName
  }, [team?.videographer_users, isTeamRepOnTeam])

  const imageSrc = useMemo(() => {
    const videographers = team?.videographer_users
    if (!videographers || videographers.length === 0) {
      return PLACEHOLDER_PLAYER
    }
    return videographers[0].avatar_url || PLACEHOLDER_PLAYER
  }, [team?.videographer_users])

  if (!showVideo) {
    return null
  }

  return (
    <div css={snacksContainerStyle(isTeamRepOnTeam)} onClick={handleClick}>
      <img
        css={snacksImageStyle}
        src={imageSrc}
        alt='Videographer'
        onError={(e) => {
          e.currentTarget.src = PLACEHOLDER_PLAYER
        }}
      />
      <div css={snacksLabelAndTextContainerStyle}>
        <p css={snacksLabelStyle}>Videographer {teamNameSuffix}</p>
        <p css={snacksTextStyle}>{displayText}</p>
      </div>
      {isTeamRepOnTeam ? <SwapOutlined css={styledSwapOutlinedStyle} /> : null}
    </div>
  )
}

export default React.memo(VideographerInfo)
